import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Badge, DropDown, Box, Button, toast } from "dash-ui";
import { CmsStatusEnum } from "dash-common";
import {
  useUpdateCmsStatusMutation,
  GetPropertiesDocument,
  CmsStatusEnum as GraphQLCmsStatusEnum,
} from "gql/generated.graphql";
import {
  CmsStatusDropdownProps,
  STATUS_TO_BADGE_TYPE,
  DROPDOWN_WIDTH,
  BADGE_HEIGHT,
  DEFAULT_MESSAGES,
} from "./CmsStatusDropdown.constants";

export const CmsStatusDropdown = ({
  propertyId,
  currentStatus,
  onStatusChange,
}: CmsStatusDropdownProps) => {
  const { t: tLabels } = useTranslation("common", { keyPrefix: "labels" });
  const { t } = useTranslation(["common", "pages"]);
  const [updateCmsStatus, { loading }] = useUpdateCmsStatusMutation({
    refetchQueries: [GetPropertiesDocument],
  });

  const handleStatusChange = useCallback(
    async (newStatus: CmsStatusEnum, closeDropdown: () => void) => {
      try {
        await updateCmsStatus({
          variables: {
            input: {
              propertyId,
              status: newStatus as unknown as GraphQLCmsStatusEnum,
            },
          },
        });

        toast.success(
          t("pages:propertyPage.cmsStatusUpdateSuccess") ||
            DEFAULT_MESSAGES.updateSuccess
        );

        closeDropdown();
        onStatusChange?.();
      } catch (error: any) {
        // Show specific error messages for validation errors
        let errorMessage = error.message;

        if (error.message.includes("Cannot change status from Proposed")) {
          // Extract the specific requirement from the error message
          if (error.message.includes("Primary organization is required")) {
            errorMessage =
              "Cannot change status: Primary organization is required. Please add an organization in the property details first.";
          } else if (
            error.message.includes("location information is required")
          ) {
            errorMessage =
              "Cannot change status: Complete location information is required. Please add a valid address in the property details first.";
          } else {
            errorMessage = error.message.replace(
              "Cannot change status from Proposed: ",
              "Cannot change status: "
            );
          }
        }

        toast.error(
          errorMessage ||
            t("pages:propertyPage.cmsStatusUpdateError") ||
            DEFAULT_MESSAGES.updateError
        );

        console.error("Failed to update CMS status:", error);
      }
    },
    [propertyId, updateCmsStatus, onStatusChange, t]
  );

  const label = currentStatus
    ? tLabels(currentStatus.toLowerCase())
    : tLabels("notSet") || DEFAULT_MESSAGES.notSet;

  const badgeType = currentStatus
    ? STATUS_TO_BADGE_TYPE[currentStatus]
    : "neutral";

  return (
    <Box w={DROPDOWN_WIDTH}>
      <DropDown
        w={DROPDOWN_WIDTH}
        Anchor={({ isOpen }) => (
          <Badge
            h={BADGE_HEIGHT}
            w={DROPDOWN_WIDTH}
            square
            loading={loading}
            type={badgeType}
            iconRight={isOpen ? "arrowUp" : "arrowDown"}
            title={label}
          />
        )}
        optionsStyle={{
          maxWidth: "none",
        }}
      >
        {({ closeDropdown }) => (
          <Box py={1} style={{ width: `${DROPDOWN_WIDTH}px` }}>
            {Object.values(CmsStatusEnum).map((status) => {
              const isCurrentStatus = status === currentStatus;
              const statusLabel = tLabels(status.toLowerCase());

              return (
                <Box
                  key={status}
                  w="100%"
                  style={{
                    backgroundColor: isCurrentStatus
                      ? "#E3F2FD"
                      : "transparent",
                  }}
                >
                  <Button
                    variation="flat"
                    w="100%"
                    px={3}
                    py={2}
                    onClick={() => handleStatusChange(status, closeDropdown)}
                    disabled={loading}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      <span>{statusLabel}</span>
                    </span>
                  </Button>
                </Box>
              );
            })}
          </Box>
        )}
      </DropDown>
    </Box>
  );
};

export default CmsStatusDropdown;
